// Models
import {
  ResourceDropdownItem,
  SearchTypes,
  TypeDropdownOption,
} from 'app/modules/search/models';
import { PaginationPayload } from 'app/shared/pagination/models';

// Helpers
import {
  formatActionEventsTopBarSearch,
  formatAlertTopBarSearch,
  formatCaseTopBarSearch,
  formatEntityTopBarSearch,
  formatEventTopBarSearch,
  formatFlowTopBarSearch,
  formatInstrumentTopBarSearch,
  formatNotesTopBarSearch,
  formatRuleTopBarSearch,
  formatTeamTopBarSearch,
} from 'app/modules/search/helpers';

// Utils
import permissions from 'app/shared/utils/permissions';
import {
  IconBriefcase,
  IconCoin,
  IconCreditCard,
  IconFlag,
  IconGitFork,
  IconLock,
  IconPencil,
  IconScale,
  IconUser,
  IconUsers,
} from '@u21/tabler-icons';
import {
  RuleSearchOptions,
  CaseSearchOptions,
  AlertSearchOptions,
  EntitySearchOptions,
  MatchType,
  TeamSearchOptions,
  OldEntitySearchType,
  NotesSearchOptions,
} from 'app/modules/search/enum';
import { SearchCondition } from 'app/modules/search/payload';

export const UNIT21_ID_FIELD_NOT_FOUND = 'UNIT21_ID_NOT_FOUND';

export const DEFAULT_SEARCH_PAGINATION_PAYLOAD: PaginationPayload = {
  offset: 1,
  limit: 5,
};

export const EVENTS_TYPE_OPTIONS: TypeDropdownOption[] = [
  {
    key: 'Event ID',
    text: 'Event ID',
    value: 'external_id',
    placeholder: 'external id (requires exact match)',
  },
];

export const ACTION_EVENTS_TYPE_OPTIONS: TypeDropdownOption[] = [
  {
    key: 'Event ID',
    text: 'Event ID',
    value: 'external_id',
    placeholder: 'external id (requires exact match)',
  },
];

export const INSTRUMENTS_TYPE_OPTIONS: TypeDropdownOption[] = [
  {
    key: 'Instrument ID',
    text: 'Instrument ID',
    value: 'external_id',
    placeholder: 'instrument id',
  },
];

export const ENTITIES_TYPE_OPTIONS: TypeDropdownOption[] = [
  {
    key: 'Entity ID',
    text: 'Entity ID',
    value: OldEntitySearchType.EXTERNAL_ID,
    placeholder: 'entity id',
  },
  {
    key: 'Business Name',
    text: 'Business Name',
    value: OldEntitySearchType.NAME,
    placeholder: 'business name',
  },
  {
    key: 'User Name',
    text: 'User Name',
    value: OldEntitySearchType.USER_NAME,
    placeholder: 'user name (first name, last name or full name)',
  },
  {
    key: 'Email Address',
    text: 'Email Address',
    value: OldEntitySearchType.EMAIL_ADDRESS,
    placeholder: 'email address',
  },
  {
    key: 'Phone Numbers',
    text: 'Phone Numbers',
    value: OldEntitySearchType.PHONE_NUMBERS,
    placeholder: 'phone numbers',
  },
];

export const ENTITY_SEARCH_TYPE_OPTIONS: TypeDropdownOption[] = [
  {
    key: 'Exact Entity ID',
    text: 'Exact Entity ID',
    value: OldEntitySearchType.EXACT_EXTERNAL_ID,
    placeholder: 'exact entity id',
  },
  ...ENTITIES_TYPE_OPTIONS,
];

export const RESOURCE_DROPDOWN_ITEMS: ResourceDropdownItem[] = [
  {
    icon: IconUser,
    text: 'Entities',
    value: SearchTypes.ENTITIES,
    placeholder: 'Search for entities by',
    formatSearchResult: formatEntityTopBarSearch,
    permissions: [permissions.readEntities],
  },
  {
    icon: IconCoin,
    text: 'Transactions',
    value: SearchTypes.TRANSACTIONS,
    placeholder: 'Search for transactions by',
    formatSearchResult: formatEventTopBarSearch,
    permissions: [permissions.readEvents],
  },
  {
    icon: IconLock,
    text: 'Action Events',
    value: SearchTypes.ACTION_EVENTS,
    placeholder: 'Search for action events by',
    formatSearchResult: formatActionEventsTopBarSearch,
    permissions: [permissions.readEvents],
  },
  {
    icon: IconCreditCard,
    text: 'Instruments',
    value: SearchTypes.INSTRUMENTS,
    placeholder: 'Search for instruments by',
    formatSearchResult: formatInstrumentTopBarSearch,
    permissions: [permissions.readTxnInstruments],
  },
  {
    icon: IconScale,
    text: 'Rules',
    value: SearchTypes.RULES,
    placeholder: 'Search for rules by id, title or description',
    formatSearchResult: formatRuleTopBarSearch,
    permissions: [permissions.readRules],
  },
  {
    icon: IconGitFork,
    text: 'Flows',
    value: SearchTypes.FLOWS,
    placeholder: 'Search for flows by id, title or description',
    formatSearchResult: formatFlowTopBarSearch,
    permissions: [permissions.readRules],
  },
  {
    icon: IconFlag,
    text: 'Alerts',
    value: SearchTypes.ALERTS,
    placeholder: 'Search for alerts by id, external id, title or description',
    formatSearchResult: formatAlertTopBarSearch,
    permissions: [permissions.readAlerts],
  },
  {
    icon: IconBriefcase,
    text: 'Cases',
    value: SearchTypes.CASES,
    placeholder: 'Search for cases by id, title or description',
    formatSearchResult: formatCaseTopBarSearch,
    permissions: [permissions.readCases],
  },
  {
    icon: IconUsers,
    text: 'Teams',
    value: SearchTypes.TEAMS,
    placeholder: 'Search for teams by id, name or description',
    formatSearchResult: formatTeamTopBarSearch,
    permissions: [permissions.readTeams],
  },
  {
    icon: IconPencil,
    text: 'Notes',
    value: SearchTypes.NOTES,
    placeholder: 'Search for notes by',
    formatSearchResult: formatNotesTopBarSearch,
    permissions: [
      permissions.readAlerts,
      permissions.readCases,
      permissions.readRules,
      permissions.readSars,
      permissions.readTxnInstruments,
      permissions.readEntities,
    ],
  },
];

export const RESOURCE_TO_TYPE_DROPDOWN_OPTIONS: Partial<
  Record<SearchTypes, TypeDropdownOption[]>
> = {
  [SearchTypes.ENTITIES]: [...ENTITIES_TYPE_OPTIONS],
  [SearchTypes.TRANSACTIONS]: [...EVENTS_TYPE_OPTIONS],
  [SearchTypes.ACTION_EVENTS]: [...ACTION_EVENTS_TYPE_OPTIONS],
  [SearchTypes.INSTRUMENTS]: [...INSTRUMENTS_TYPE_OPTIONS],
};

export const ENTITY_SEARCH_CONDITIONS: Record<
  OldEntitySearchType,
  Omit<SearchCondition<EntitySearchOptions>, 'phrases'>[]
> = {
  [OldEntitySearchType.EXACT_EXTERNAL_ID]: [
    {
      field: EntitySearchOptions.EXTERNAL_ID,
      match_type: MatchType.EXACT,
    },
  ],
  [OldEntitySearchType.EXTERNAL_ID]: [
    {
      field: EntitySearchOptions.EXTERNAL_ID,
      match_type: MatchType.PARTIAL,
    },
  ],
  [OldEntitySearchType.NAME]: [
    {
      field: EntitySearchOptions.BUSINESS_NAME,
      match_type: MatchType.PARTIAL,
    },
  ],
  // For user name searches, search across multiple fields in a single condition
  // This is not optimal for getting the best search results, but mimics the v1 behavior
  // Ticket with details: https://app.shortcut.com/unit21/story/89520/unit21-search-v1-not-behaving-properly-see-if-search-v2-would-return-this-result
  [OldEntitySearchType.USER_NAME]: [
    {
      field: EntitySearchOptions.USER_NAME,
      match_type: MatchType.PARTIAL,
    },
    {
      field: EntitySearchOptions.FIRST_NAME,
      match_type: MatchType.PARTIAL,
    },
    {
      field: EntitySearchOptions.MIDDLE_NAME,
      match_type: MatchType.PARTIAL,
    },
    {
      field: EntitySearchOptions.LAST_NAME,
      match_type: MatchType.PARTIAL,
    },
  ],
  [OldEntitySearchType.EMAIL_ADDRESS]: [
    {
      field: EntitySearchOptions.EMAIL_ADDRESSES,
      match_type: MatchType.PARTIAL,
    },
  ],
  [OldEntitySearchType.PHONE_NUMBERS]: [
    {
      field: EntitySearchOptions.PHONE_NUMBERS,
      match_type: MatchType.EXACT,
    },
  ],
} as const;

export const RULE_SEARCH_CONDITIONS: Omit<
  SearchCondition<RuleSearchOptions>,
  'phrases'
>[] = [
  {
    field: RuleSearchOptions.ID,
    match_type: MatchType.EXACT,
  },
  {
    field: RuleSearchOptions.TITLE,
    match_type: MatchType.PARTIAL,
  },
  {
    field: RuleSearchOptions.DESCRIPTION,
    match_type: MatchType.PARTIAL,
  },
] as const;

export const CASE_SEARCH_CONDITIONS: Omit<
  SearchCondition<CaseSearchOptions>,
  'phrases'
>[] = [
  {
    field: CaseSearchOptions.ID,
    match_type: MatchType.EXACT,
  },
  {
    field: CaseSearchOptions.TITLE,
    match_type: MatchType.PARTIAL,
  },
  {
    field: CaseSearchOptions.DESCRIPTION,
    match_type: MatchType.PARTIAL,
  },
] as const;

export const ALERT_SEARCH_CONDITIONS: Omit<
  SearchCondition<AlertSearchOptions>,
  'phrases'
>[] = [
  {
    field: AlertSearchOptions.ID,
    match_type: MatchType.EXACT,
  },
  {
    field: AlertSearchOptions.EXTERNAL_ID,
    match_type: MatchType.EXACT,
  },
  {
    field: AlertSearchOptions.TITLE,
    match_type: MatchType.PARTIAL,
  },
  {
    field: AlertSearchOptions.DESCRIPTION,
    match_type: MatchType.PARTIAL,
  },
] as const;

export const TEAM_SEARCH_CONDITIONS: Omit<
  SearchCondition<TeamSearchOptions>,
  'phrases'
>[] = [
  {
    field: TeamSearchOptions.ID,
    match_type: MatchType.EXACT,
  },
  {
    field: TeamSearchOptions.NAME,
    match_type: MatchType.PARTIAL,
  },
  {
    field: TeamSearchOptions.DESCRIPTION,
    match_type: MatchType.PARTIAL,
  },
] as const;

export const NOTES_SEARCH_CONDITIONS: Omit<
  SearchCondition<NotesSearchOptions>,
  'phrases'
>[] = [
  {
    field: NotesSearchOptions.CONTENT,
    match_type: MatchType.PARTIAL,
  },
] as const;

export const CGDO_SET: Readonly<Set<SearchTypes>> = new Set([
  SearchTypes.ENTITIES,
  SearchTypes.TRANSACTIONS,
  SearchTypes.ACTION_EVENTS,
  SearchTypes.INSTRUMENTS,
]);
